import React from "react";
import { Select, Button, Space, Divider } from "antd";
import { CheckSquareOutlined, BorderOutlined } from "@ant-design/icons";

const { Option } = Select;

/**
 * StudentMultiSelect Component
 *
 * A reusable multi-select dropdown with "Select All" / "Deselect All" functionality
 * Wraps Ant Design's Select component with additional features for bulk selection
 *
 * @param {Array} students - Array of student objects
 * @param {Array} value - Currently selected values
 * @param {Function} onChange - Callback when selection changes
 * @param {String} placeholder - Placeholder text
 * @param {Boolean} loading - Loading state
 * @param {Boolean} disabled - Disabled state
 * @param {Function} filterOption - Custom filter function
 * @param {String} notFoundContent - Content to show when no options found
 * @param {Function} getStudentDisplay - Function to format student display (optional)
 * @param {Function} getStudentValue - Function to get student value (optional)
 * @param {Object} style - Additional styles
 */
const StudentMultiSelect = ({
  students = [],
  value = [],
  onChange,
  placeholder = "Select students",
  loading = false,
  disabled = false,
  filterOption,
  notFoundContent,
  getStudentDisplay,
  getStudentValue,
  style = {},
  ...restProps
}) => {
  // Default function to get student display text
  const defaultGetStudentDisplay = (student) => {
    const fullName = [
      student.firstName || student.first_name,
      student.middleName || student.middle_name,
      student.lastName || student.last_name,
    ]
      .filter(Boolean)
      .join(" ");
    const spc = student.spcCode || student.spc || student.id;
    const className = student.className || student.class_name;

    return `${fullName} (${spc || "No SPC"})${className ? ` - ${className}` : ""}`;
  };

  // Default function to get student value
  const defaultGetStudentValue = (student) => {
    return student.spcCode || student.spc || student.id;
  };

  const displayFn = getStudentDisplay || defaultGetStudentDisplay;
  const valueFn = getStudentValue || defaultGetStudentValue;

  // Handle select all
  const handleSelectAll = () => {
    const allValues = students.map((student) => valueFn(student));
    onChange(allValues);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    onChange([]);
  };

  // Check if all students are selected
  const allSelected = students.length > 0 && value.length === students.length;

  // Custom dropdown render to add Select All / Deselect All button
  const dropdownRender = (menu) => (
    <>
      {students.length > 0 && (
        <>
          <div style={{ padding: "8px 12px" }}>
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              {allSelected ? (
                <Button
                  type="link"
                  size="small"
                  icon={<BorderOutlined />}
                  onClick={handleDeselectAll}
                  style={{ padding: 0, height: "auto" }}
                >
                  Deselect All
                </Button>
              ) : (
                <Button
                  type="link"
                  size="small"
                  icon={<CheckSquareOutlined />}
                  onClick={handleSelectAll}
                  style={{ padding: 0, height: "auto" }}
                >
                  Select All ({students.length})
                </Button>
              )}
              <span style={{ color: "#999", fontSize: "12px" }}>
                {value.length} / {students.length} selected
              </span>
            </Space>
          </div>
          <Divider style={{ margin: "4px 0" }} />
        </>
      )}
      {menu}
    </>
  );

  return (
    <Select
      mode="multiple"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      showSearch
      optionFilterProp="children"
      loading={loading}
      disabled={disabled}
      notFoundContent={notFoundContent}
      filterOption={
        filterOption ||
        ((input, option) => {
          const label = String(option.children || "");
          return label.toLowerCase().includes(input.toLowerCase());
        })
      }
      dropdownRender={dropdownRender}
      maxTagCount="responsive"
      style={style}
      {...restProps}
    >
      {students.map((student) => {
        const value = valueFn(student);
        const display = displayFn(student);
        return (
          <Option key={value} value={value}>
            {display}
          </Option>
        );
      })}
    </Select>
  );
};

export default StudentMultiSelect;
